// Chat Theme Design System
// Based on the light and dark theme guides

export interface ChatTheme {
  name: 'light' | 'dark';
  colors: {
    // Backgrounds
    pageBackground: string;
    containerBackground: string;
    headerBackground: string;
    messagesBackground: string;
    inputBackground: string;
    inputWrapperBackground: string;
    inputWrapperFocusBackground: string;
    
    // Text
    primaryText: string;
    secondaryText: string;
    mutedText: string;
    placeholderText: string;
    
    // Message bubbles
    receivedBubbleBackground: string;
    sentBubbleBackground: string;
    receivedBubbleText: string;
    sentBubbleText: string;
    
    // Accents
    accent: string;
    accentHover: string;
    statusDot: string;
    
    // Borders
    borderDefault: string;
    borderHover: string;
    borderFocus: string;
    
    // Typing indicator
    typingBackground: string;
    typingDot: string;
    
    // Scrollbar
    scrollbarTrack: string;
    scrollbarThumb: string;
    scrollbarThumbHover: string;
  };
  shadows: {
    // Container shadows
    container: string;
    containerHover: string;
    
    // Header/Input shadows
    header: string;
    input: string;
    inputWrapper: string;
    inputWrapperFocus: string;
    
    // Button shadows
    sendButton: string;
    sendButtonHover: string;
    sendButtonActive: string;
    
    // Message shadows
    receivedBubble: string;
    sentBubble: string;
    receivedBubbleHover: string;
    sentBubbleHover: string;
    
    // Other elements
    avatar: string;
    statusDot: string;
    typingIndicator: string;
  };
  animations: {
    fadeInUp: string;
    pulse: string;
    typing: string;
  };
  borderRadius: {
    container: string;
    bubble: string;
    input: string;
    button: string;
    avatar: string;
  };
  spacing: {
    containerPadding: string;
    messagePadding: string;
    messageGap: string;
    headerPadding: string;
    inputPadding: string;
  };
  typography: {
    fontFamily: string;
    messageFontSize: string;
    timeFontSize: string;
    headerFontSize: string;
    inputFontSize: string;
  };
  transitions: {
    default: string;
    fast: string;
    smooth: string;
  };
}

export const lightTheme: ChatTheme = {
  name: 'light',
  colors: {
    // Backgrounds
    pageBackground: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecf5 100%)',
    containerBackground: '#ffffff',
    headerBackground: 'rgba(255, 255, 255, 0.98)',
    messagesBackground: '#f8f9fb',
    inputBackground: 'rgba(255, 255, 255, 0.98)',
    inputWrapperBackground: '#f0f3f7',
    inputWrapperFocusBackground: '#ffffff',
    
    // Text
    primaryText: '#2c3e50',
    secondaryText: '#4361ee',
    mutedText: '#95a5b8',
    placeholderText: '#95a5b8',
    
    // Message bubbles
    receivedBubbleBackground: '#e9ecf5',
    sentBubbleBackground: '#4361ee',
    receivedBubbleText: '#2c3e50',
    sentBubbleText: '#ffffff',
    
    // Accents
    accent: '#4361ee',
    accentHover: '#5a73f5',
    statusDot: '#4361ee',
    
    // Borders
    borderDefault: 'rgba(0, 0, 0, 0.06)',
    borderHover: 'rgba(67, 97, 238, 0.2)',
    borderFocus: '#4361ee',
    
    // Typing indicator
    typingBackground: '#e9ecf5',
    typingDot: '#95a5b8',
    
    // Scrollbar
    scrollbarTrack: 'rgba(67, 97, 238, 0.04)',
    scrollbarThumb: 'rgba(67, 97, 238, 0.2)',
    scrollbarThumbHover: 'rgba(67, 97, 238, 0.4)',
  },
  shadows: {
    // Container shadows
    container: '0 0 60px rgba(67, 97, 238, 0.12), 0 20px 80px rgba(0, 0, 0, 0.12), 0 10px 40px rgba(0, 0, 0, 0.08)',
    containerHover: '0 0 80px rgba(67, 97, 238, 0.15), 0 25px 100px rgba(0, 0, 0, 0.15), 0 15px 50px rgba(0, 0, 0, 0.1)',
    
    // Header/Input shadows
    header: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    input: '0 -8px 30px rgba(0, 0, 0, 0.08), 0 -4px 15px rgba(0, 0, 0, 0.04)',
    inputWrapper: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 3px rgba(0, 0, 0, 0.04)',
    inputWrapperFocus: '0 0 0 4px rgba(67, 97, 238, 0.15), 0 6px 25px rgba(0, 0, 0, 0.1), 0 3px 10px rgba(0, 0, 0, 0.06)',
    
    // Button shadows
    sendButton: '0 6px 20px rgba(67, 97, 238, 0.35), 0 3px 10px rgba(67, 97, 238, 0.25)',
    sendButtonHover: '0 8px 25px rgba(67, 97, 238, 0.45), 0 4px 12px rgba(67, 97, 238, 0.35)',
    sendButtonActive: '0 2px 8px rgba(67, 97, 238, 0.3)',
    
    // Message shadows
    receivedBubble: '0 4px 15px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    sentBubble: '0 6px 25px rgba(67, 97, 238, 0.3), 0 3px 12px rgba(67, 97, 238, 0.2)',
    receivedBubbleHover: '0 8px 25px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)',
    sentBubbleHover: '0 10px 35px rgba(67, 97, 238, 0.4), 0 5px 15px rgba(67, 97, 238, 0.3)',
    
    // Other elements
    avatar: '0 4px 20px rgba(67, 97, 238, 0.25)',
    statusDot: '0 0 10px rgba(67, 97, 238, 0.5)',
    typingIndicator: '0 4px 15px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  animations: {
    fadeInUp: `@keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }`,
    pulse: `@keyframes pulse {
      0% {
        box-shadow: 0 0 10px rgba(67, 97, 238, 0.5);
      }
      50% {
        box-shadow: 0 0 20px rgba(67, 97, 238, 0.8);
      }
      100% {
        box-shadow: 0 0 10px rgba(67, 97, 238, 0.5);
      }
    }`,
    typing: `@keyframes typing {
      0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.5;
      }
      30% {
        transform: translateY(-10px);
        opacity: 1;
      }
    }`,
  },
  borderRadius: {
    container: '24px',
    bubble: '24px',
    input: '28px',
    button: '50%',
    avatar: '50%',
  },
  spacing: {
    containerPadding: '0',
    messagePadding: '24px',
    messageGap: '20px',
    headerPadding: '20px 24px',
    inputPadding: '20px 24px 24px',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    messageFontSize: '14px',
    timeFontSize: '11px',
    headerFontSize: '16px',
    inputFontSize: '14px',
  },
  transitions: {
    default: 'none',
    fast: 'none',
    smooth: 'none',
  },
};

export const darkTheme: ChatTheme = {
  name: 'dark',
  colors: {
    // Backgrounds
    pageBackground: 'linear-gradient(135deg, #0a0a0a 0%, #141420 100%)',
    containerBackground: '#1a1d29',
    headerBackground: 'rgba(26, 29, 41, 0.98)',
    messagesBackground: '#15171f',
    inputBackground: 'rgba(26, 29, 41, 0.98)',
    inputWrapperBackground: '#1e212d',
    inputWrapperFocusBackground: '#2a2d3a',
    
    // Text
    primaryText: '#ffffff',
    secondaryText: '#4361ee',
    mutedText: '#6c6f7b',
    placeholderText: '#6c6f7b',
    
    // Message bubbles
    receivedBubbleBackground: '#2d3142',
    sentBubbleBackground: '#4361ee',
    receivedBubbleText: '#e0e0e0',
    sentBubbleText: '#ffffff',
    
    // Accents
    accent: '#4361ee',
    accentHover: '#5a73f5',
    statusDot: '#4361ee',
    
    // Borders
    borderDefault: 'rgba(0, 0, 0, 0.5)',
    borderHover: 'rgba(67, 97, 238, 0.3)',
    borderFocus: '#4361ee',
    
    // Typing indicator
    typingBackground: '#2d3142',
    typingDot: '#8b8d95',
    
    // Scrollbar
    scrollbarTrack: 'rgba(255, 255, 255, 0.02)',
    scrollbarThumb: 'rgba(67, 97, 238, 0.3)',
    scrollbarThumbHover: 'rgba(67, 97, 238, 0.5)',
  },
  shadows: {
    // Container shadows
    container: '0 0 80px rgba(67, 97, 238, 0.2), 0 30px 90px rgba(0, 0, 0, 0.5), 0 12px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    containerHover: '0 0 100px rgba(67, 97, 238, 0.3), 0 35px 100px rgba(0, 0, 0, 0.6), 0 15px 50px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    
    // Header/Input shadows
    header: '0 4px 30px rgba(0, 0, 0, 0.4), 0 2px 10px rgba(0, 0, 0, 0.3)',
    input: '0 -8px 40px rgba(0, 0, 0, 0.5), 0 -4px 20px rgba(0, 0, 0, 0.3)',
    inputWrapper: '0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 10px rgba(0, 0, 0, 0.2), inset 0 2px 5px rgba(0, 0, 0, 0.3)',
    inputWrapperFocus: '0 0 0 4px rgba(67, 97, 238, 0.2), 0 6px 30px rgba(0, 0, 0, 0.4), 0 3px 15px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(67, 97, 238, 0.05)',
    
    // Button shadows
    sendButton: '0 8px 25px rgba(67, 97, 238, 0.4), 0 4px 12px rgba(67, 97, 238, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    sendButtonHover: '0 12px 35px rgba(67, 97, 238, 0.5), 0 6px 20px rgba(67, 97, 238, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    sendButtonActive: '0 4px 15px rgba(67, 97, 238, 0.3), 0 2px 8px rgba(67, 97, 238, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.2)',
    
    // Message shadows
    receivedBubble: '0 8px 30px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    sentBubble: '0 10px 40px rgba(67, 97, 238, 0.4), 0 4px 20px rgba(67, 97, 238, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    receivedBubbleHover: '0 12px 40px rgba(0, 0, 0, 0.5), 0 6px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    sentBubbleHover: '0 15px 50px rgba(67, 97, 238, 0.5), 0 6px 25px rgba(67, 97, 238, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    
    // Other elements
    avatar: '0 6px 25px rgba(67, 97, 238, 0.4), 0 3px 12px rgba(67, 97, 238, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    statusDot: '0 0 15px rgba(67, 97, 238, 0.6), 0 0 30px rgba(67, 97, 238, 0.4)',
    typingIndicator: '0 8px 30px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  },
  animations: {
    fadeInUp: `@keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
        filter: blur(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
        filter: blur(0);
      }
    }`,
    pulse: `@keyframes pulse {
      0% {
        box-shadow: 0 0 15px rgba(67, 97, 238, 0.6), 0 0 30px rgba(67, 97, 238, 0.4);
      }
      50% {
        box-shadow: 0 0 25px rgba(67, 97, 238, 0.9), 0 0 50px rgba(67, 97, 238, 0.6);
      }
      100% {
        box-shadow: 0 0 15px rgba(67, 97, 238, 0.6), 0 0 30px rgba(67, 97, 238, 0.4);
      }
    }`,
    typing: `@keyframes typing {
      0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.4;
        box-shadow: 0 0 5px rgba(67, 97, 238, 0.2);
      }
      30% {
        transform: translateY(-10px);
        opacity: 1;
        box-shadow: 0 0 15px rgba(67, 97, 238, 0.6);
      }
    }`,
  },
  borderRadius: {
    container: '24px',
    bubble: '24px',
    input: '28px',
    button: '50%',
    avatar: '50%',
  },
  spacing: {
    containerPadding: '0',
    messagePadding: '24px',
    messageGap: '20px',
    headerPadding: '20px 24px',
    inputPadding: '20px 24px 24px',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    messageFontSize: '14px',
    timeFontSize: '11px',
    headerFontSize: '16px',
    inputFontSize: '14px',
  },
  transitions: {
    default: 'none',
    fast: 'none',
    smooth: 'none',
  },
};

// Helper function to get theme
export const getTheme = (themeName: 'light' | 'dark'): ChatTheme => {
  return themeName === 'dark' ? darkTheme : lightTheme;
};

// CSS string generator for injecting theme styles
export const generateThemeCSS = (theme: ChatTheme): string => {
  return `
    /* Animations */
    ${theme.animations.fadeInUp}
    ${theme.animations.pulse}
    ${theme.animations.typing}
    
    /* Custom scrollbar */
    .chat-messages::-webkit-scrollbar {
      width: 8px;
    }
    
    .chat-messages::-webkit-scrollbar-track {
      background: ${theme.colors.scrollbarTrack};
      border-radius: 4px;
      ${theme.name === 'dark' ? 'box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);' : ''}
    }
    
    .chat-messages::-webkit-scrollbar-thumb {
      background: ${theme.colors.scrollbarThumb};
      border-radius: 4px;
      ${theme.name === 'dark' ? 'box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);' : ''}
    }
    
    .chat-messages::-webkit-scrollbar-thumb:hover {
      background: ${theme.colors.scrollbarThumbHover};
      ${theme.name === 'dark' ? 'box-shadow: 0 2px 6px rgba(67, 97, 238, 0.4);' : ''}
    }
  `;
};